
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThumbsUp, ThumbsDown, Smile, Meh, Frown, CheckCircle2 } from 'lucide-react';
import { Language } from '@/types';

interface NovaPageFeedbackProps {
  lang: Language;
  pageId: string;
}

const NovaPageFeedback: React.FC<NovaPageFeedbackProps> = ({ lang, pageId }) => {
  const [submitted, setSubmitted] = useState(false);
  const isRtl = lang === 'ar';

  useEffect(() => {
    const feedbackGiven = localStorage.getItem(`feedback_${pageId}`);
    if (feedbackGiven) setSubmitted(true);
  }, [pageId]);

  const handleFeedback = (type: 'up' | 'down') => {
    // In a real app, you would send this to Supabase/API
    localStorage.setItem(`feedback_${pageId}`, type);
    setSubmitted(true);
  };

  return (
    <div className="mt-24 pt-12 border-t border-white/5">
      <AnimatePresence mode="wait">
        {!submitted ? (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`flex flex-col md:flex-row items-center justify-between gap-6 p-8 rounded-3xl bg-white/[0.02] border border-white/5 ${isRtl ? 'md:flex-row-reverse' : ''}`}
          >
            <div>
              <h4 className="text-white font-medium text-lg mb-1">
                {lang === 'en' ? 'Was this page helpful?' : 'هل كانت هذه الصفحة مفيدة؟'}
              </h4>
              <p className="text-sm text-slate-500">
                {lang === 'en' ? 'Your feedback helps us improve the documentation.' : 'ملاحظاتك تساعدنا في تحسين الدليل.'}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button 
                onClick={() => handleFeedback('up')}
                className="group flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/[0.03] border border-white/5 hover:border-emerald-500/50 hover:bg-emerald-500/10 transition-all"
              >
                <Smile size={20} className="text-slate-400 group-hover:text-emerald-500 transition-colors" />
                <span className="text-sm font-medium text-slate-300 group-hover:text-emerald-400">
                  {lang === 'en' ? 'Yes' : 'نعم'}
                </span>
              </button>

              <button 
                onClick={() => handleFeedback('down')}
                className="group flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/[0.03] border border-white/5 hover:border-rose-500/50 hover:bg-rose-500/10 transition-all"
              >
                <Frown size={20} className="text-slate-400 group-hover:text-rose-500 transition-colors" />
                <span className="text-sm font-medium text-slate-300 group-hover:text-rose-400">
                  {lang === 'en' ? 'No' : 'لا'}
                </span>
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center p-12 rounded-3xl bg-[#7F77DD]/5 border border-[#7F77DD]/10 text-center"
          >
            <div className="w-12 h-12 rounded-full bg-[#7F77DD]/20 flex items-center justify-center mb-4">
              <CheckCircle2 size={24} className="text-[#7F77DD]" />
            </div>
            <h4 className="text-white font-medium text-lg mb-1">
              {lang === 'en' ? 'Thank you for your feedback!' : 'شكراً جزيلاً لملاحظاتك!'}
            </h4>
            <p className="text-sm text-slate-400">
              {lang === 'en' ? 'We appreciate your help in making Nova better.' : 'نحن نقدر مساعدتك في جعل نوفا أفضل.'}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NovaPageFeedback;
